<!-- eslint-disable perfectionist/sort-modules -->
<script setup lang="ts">
import { Download, Plus } from 'lucide-vue-next';
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';
import { toast } from 'vue3-toastify';
import { useRouter } from 'vue-router';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import { campaignManagementService } from '~/services/campaign-management-api/campaign-management-api';

import type {
  Campaign, CampaignFilters as CampaignFiltersType, CampaignKpis, FilterSelectOption,
} from '../types';

import CampaignFilters from '../components/CampaignFilters.vue';
import CampaignKpiCards from '../components/CampaignKpiCards.vue';
import CampaignTable from '../components/CampaignTable.vue';
import { DEFAULT_PAGE_SIZE } from '../constants';
import {
  mapCampaignFiltersToApiParams,
  mapCampaignListResponse,
  mapIncentiveSelectOptions,
} from '../mappers/campaignMappers';

const router = useRouter();

const isLoading = ref(true);
const campaigns = ref<Campaign[]>([]);
const incentiveOptions = ref<FilterSelectOption[]>([]);
const filters = ref<CampaignFiltersType>({ incentiveId: '', search: '', status: '' });
const kpis = ref<CampaignKpis | null>(null);
const currentPage = ref(1);
const pageSize = ref(DEFAULT_PAGE_SIZE);
const totalCount = ref(0);
const totalPages = ref(0);

const hasNext = computed(() => currentPage.value < totalPages.value);

async function loadCampaigns() {
  isLoading.value = true;

  try {
    const data = await campaignManagementService.listCampaigns({
      page: currentPage.value,
      page_size: pageSize.value,
      ...mapCampaignFiltersToApiParams(filters.value),
    });

    const mapped = mapCampaignListResponse(data);
    campaigns.value = mapped.campaigns;
    kpis.value = mapped.kpis;
    totalCount.value = mapped.total;
    totalPages.value = mapped.pages;
    currentPage.value = mapped.page;
  } catch {
    toast.error('Não foi possível carregar as campanhas. Tente novamente.');
  } finally {
    isLoading.value = false;
  }
}

async function loadIncentiveOptions() {
  try {
    const data = await campaignManagementService.listIncentives({
      page: 1,
      page_size: 100,
      status: 'ACTIVE',
    });

    incentiveOptions.value = mapIncentiveSelectOptions(data.items);
  } catch {
    incentiveOptions.value = [];
  }
}

function reloadFromFirstPage() {
  if (currentPage.value === 1) {
    loadCampaigns();
  } else {
    currentPage.value = 1;
  }
}

function onApplyFilters(newFilters: CampaignFiltersType) {
  filters.value = newFilters;
  reloadFromFirstPage();
}

function onClearFilters() {
  filters.value = { incentiveId: '', search: '', status: '' };
  reloadFromFirstPage();
}

function onPageChange(page: number) {
  currentPage.value = page;
}

function onPageSizeChange(size: number) {
  pageSize.value = size;
  reloadFromFirstPage();
}

function onViewCampaign(campaign: Campaign) {
  router.push({ path: '/revalida-campaign-detail', query: { id: campaign.id } });
}

watch(currentPage, () => {
  loadCampaigns();
}, { immediate: true });

onMounted(() => {
  document.title = 'Campanhas Revalida';
  loadIncentiveOptions();
});
</script>

<template>
  <DashboardLayout
    title="Revalida Instalação"
    description="Campanhas Revalida"
    subtitle="Crie e gerencie campanhas/jornadas de incentivo à revalidação.
      Cada parceiro fica em apenas 1 campanha por vez."
  >
    <template #actions>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-outline-secondary" disabled>
          <Download class="icon icon-sm me-1" />
          Exportar
        </button>
        <button type="button" class="btn btn-primary" @click="router.push('/revalida-campaign-new')">
          <Plus class="icon icon-sm me-1" />
          Nova Campanha
        </button>
      </div>
    </template>

    <div class="row row-cards">
      <CampaignKpiCards
        v-if="kpis"
        :kpis="kpis"
        :is-loading="isLoading"
      />

      <CampaignFilters
        :incentive-options="incentiveOptions"
        @apply="onApplyFilters"
        @clear="onClearFilters"
      />

      <CampaignTable
        :rows="campaigns"
        :is-loading="isLoading"
        :current-page="currentPage"
        :has-next="hasNext"
        :page-size="pageSize"
        :total-count="totalCount"
        @page-change="onPageChange"
        @page-size-change="onPageSizeChange"
        @view="onViewCampaign"
      />
    </div>
  </DashboardLayout>
</template>
